import React, { useState, useRef, useEffect } from 'react';

import Button from '../../../components/ui/Button';

const CallRecordingPlayer = ({ recording, onClose }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    const audio = audioRef?.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio?.currentTime);
    const updateDuration = () => setDuration(audio?.duration);
    const handleEnded = () => setIsPlaying(false);

    audio?.addEventListener('timeupdate', updateTime);
    audio?.addEventListener('loadedmetadata', updateDuration);
    audio?.addEventListener('ended', handleEnded);

    return () => {
      audio?.removeEventListener('timeupdate', updateTime);
      audio?.removeEventListener('loadedmetadata', updateDuration);
      audio?.removeEventListener('ended', handleEnded);
    };
  }, []);

  const togglePlayPause = () => {
    const audio = audioRef?.current;
    if (isPlaying) {
      audio?.pause();
    } else {
      audio?.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (e) => {
    const audio = audioRef?.current;
    const rect = e?.currentTarget?.getBoundingClientRect();
    const percent = (e?.clientX - rect?.left) / rect?.width;
    const newTime = percent * duration;
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e?.target?.value);
    setVolume(newVolume);
    audioRef.current.volume = newVolume;
  };

  const handlePlaybackRateChange = (rate) => {
    setPlaybackRate(rate);
    audioRef.current.playbackRate = rate;
  };

  const skipTime = (seconds) => {
    const audio = audioRef?.current;
    audio.currentTime = Math.max(0, Math.min(duration, audio?.currentTime + seconds));
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds?.toString()?.padStart(2, '0')}`;
  };

  const downloadRecording = () => {
    const link = document.createElement('a');
    link.href = recording?.audioUrl;
    link.download = `arama-kaydi-${recording?.callId}.mp3`;
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-elevated max-w-2xl w-full mx-4 p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Arama Kaydı</h3>
            <p className="text-sm text-slate-600">
              {recording?.customerName} - {recording?.date} {recording?.time}
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            iconName="X"
          />
        </div>

        {/* Audio Element */}
        <audio
          ref={audioRef}
          src={recording?.audioUrl}
          preload="metadata"
        />

        {/* Recording Info */}
        <div className="bg-slate-50 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-slate-500">Arama ID:</span>
              <div className="font-medium text-slate-900">{recording?.callId}</div>
            </div>
            <div>
              <span className="text-slate-500">Süre:</span>
              <div className="font-medium text-slate-900">{recording?.duration}</div>
            </div>
            <div>
              <span className="text-slate-500">Temsilci:</span>
              <div className="font-medium text-slate-900">{recording?.agentName}</div>
            </div>
            <div>
              <span className="text-slate-500">Kalite:</span>
              <div className="font-medium text-slate-900">{recording?.quality}</div>
            </div>
          </div>
        </div>

        {/* Player Controls */}
        <div className="space-y-4">
          {/* Progress Bar */}
          <div className="space-y-2">
            <div
              className="w-full bg-slate-200 rounded-full h-2 cursor-pointer"
              onClick={handleSeek}
            >
              <div
                className="bg-primary h-2 rounded-full transition-all duration-150"
                style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
              />
            </div>
            <div className="flex justify-between text-sm text-slate-600">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          {/* Main Controls */}
          <div className="flex items-center justify-center space-x-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => skipTime(-10)}
              iconName="RotateCcw"
            />
            
            <Button
              variant="default"
              size="lg"
              onClick={togglePlayPause}
              iconName={isPlaying ? "Pause" : "Play"}
              className="w-16 h-16 rounded-full"
            />
            
            <Button
              variant="ghost"
              size="sm"
              onClick={() => skipTime(10)}
              iconName="RotateCw"
            />
          </div>

          {/* Secondary Controls */}
          <div className="flex items-center justify-between">
            {/* Volume Control */}
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowVolumeSlider(!showVolumeSlider)}
                iconName={volume === 0 ? "VolumeX" : volume < 0.5 ? "Volume1" : "Volume2"}
              />
              {showVolumeSlider && (
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={volume}
                  onChange={handleVolumeChange}
                  className="w-20 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer"
                />
              )}
            </div>

            {/* Playback Speed */}
            <div className="flex items-center space-x-2">
              <span className="text-sm text-slate-600">Hız:</span>
              <div className="flex space-x-1">
                {[0.5, 0.75, 1, 1.25, 1.5, 2]?.map((rate) => (
                  <Button
                    key={rate}
                    variant={playbackRate === rate ? "default" : "ghost"}
                    size="sm"
                    onClick={() => handlePlaybackRateChange(rate)}
                    className="text-xs px-2 py-1"
                  >
                    {rate}x
                  </Button>
                ))}
              </div>
            </div>

            {/* Download */}
            <Button
              variant="outline"
              size="sm"
              onClick={downloadRecording}
              iconName="Download"
            />
          </div>
        </div>

        {/* Recording Notes */}
        {recording?.notes && (
          <div className="mt-6 p-4 bg-slate-50 rounded-lg">
            <h4 className="text-sm font-medium text-slate-900 mb-2">Kayıt Notları</h4>
            <p className="text-sm text-slate-600">{recording?.notes}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-slate-200">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Kapat
          </Button>
          <Button
            variant="default"
            onClick={downloadRecording}
            iconName="Download"
            iconPosition="left"
          >
            İndir
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CallRecordingPlayer;